import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { getAllAdmins, deleteAdmin } from '../../services/api';
import { useAdminAuth } from '../../context/AdminAuthContext';
import '../../styles/AdminManagement.css';

interface Venue {
  id: string;
  name: string;
}

interface Admin {
  id: number;
  email: string;
  role: string;
  createdAt: string;
  venues: Venue[];
}

const AdminManagement: React.FC = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<number | null>(null);
  const history = useHistory();
  const { isSuperAdmin } = useAdminAuth();

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAdmins();
    }
  }, [isSuperAdmin]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const adminsData = await getAllAdmins();
      setAdmins(adminsData);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch admins:', err);
      setError(err.response?.data?.message || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = () => {
    history.push('/admin/create');
  };

  const confirmDelete = (adminId: number) => {
    setDeleteConfirmation(adminId);
  };

  const cancelDelete = () => {
    setDeleteConfirmation(null);
  };

  const handleDeleteAdmin = async (adminId: number) => {
    try {
      setLoading(true);
      await deleteAdmin(adminId.toString());
      setAdmins(admins.filter(admin => admin.id !== adminId));
      setDeleteConfirmation(null);
      setError(null);
    } catch (err: any) {
      console.error('Failed to delete admin:', err);
      setError(err.response?.data?.message || 'Failed to delete admin');
    } finally {
      setLoading(false);
    }
  };

  if (!isSuperAdmin) {
    return <div className="admin-management">
      <h2>Admin Management</h2>
      <p>You do not have permission to view this page.</p>
    </div>;
  }

  return (
    <div className="admin-management">
      <div className="admin-management-header">
        <h2>Admin Management</h2>
        <button 
          className="create-admin-button"
          onClick={handleCreateAdmin}
        >
          Create Admin
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading">Loading admins...</div>
      ) : admins.length === 0 ? (
        <div className="no-admins">
          <p>No admins found</p>
        </div>
      ) : (
        <div className="admins-table-container">
          <table className="admins-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Role</th>
                <th>Created At</th>
                <th>Assigned Venues</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map(admin => (
                <tr key={admin.id}>
                  <td>{admin.id}</td>
                  <td>{admin.email}</td>
                  <td>{admin.role}</td>
                  <td>{new Date(admin.createdAt).toLocaleDateString()}</td>
                  <td>
                    {admin.venues.length > 0 
                      ? admin.venues.map(venue => venue.name).join(', ')
                      : 'No venues assigned'
                    }
                  </td>
                  <td>
                    {deleteConfirmation === admin.id ? (
                      <div className="delete-confirmation">
                        <span>Are you sure?</span>
                        <button 
                          className="confirm-delete"
                          onClick={() => handleDeleteAdmin(admin.id)}
                        >
                          Yes
                        </button>
                        <button 
                          className="cancel-delete"
                          onClick={cancelDelete}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button 
                        className="delete-admin-button"
                        onClick={() => confirmDelete(admin.id)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminManagement; 